import { useCallback, useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const NumberField = ({ label, value, onChange, testid, suffix }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</span>
    <div className="mt-1.5 flex items-center gap-2">
      <input
        data-testid={testid}
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-navy focus:border-cognac focus:outline-none"
      />
      {suffix && <span className="text-sm text-slate-400">{suffix}</span>}
    </div>
  </label>
);

export const CommissionSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(() => {
    api.get("/phase3/admin/commission-settings")
      .then((res) => setSettings(res.data))
      .catch(() => toast.error("Impossible de charger les réglages"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const updateTravelTier = (index, field, value) => {
    const next = settings.travel_thresholds.map((t, i) => (i === index ? { ...t, [field]: value } : t));
    setSettings({ ...settings, travel_thresholds: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { id, updated_at, updated_by, ...body } = settings;
      const res = await api.put("/phase3/admin/commission-settings", body);
      setSettings(res.data);
      toast.success("Réglages enregistrés");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (loading || !settings) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>;

  return (
    <div data-testid="commission-settings">
      <p className="text-sm text-slate-500">Tous les taux et seuils du programme Commissions &amp; Avantages sont administrables ici — aucune valeur codée en dur.</p>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Commission entreprise</p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <NumberField label="Taux commission entreprise" suffix="%" testid="settings-rate" value={settings.commission_rate_pct}
            onChange={(v) => setSettings({ ...settings, commission_rate_pct: v })} />
          <NumberField label="Part reversée au partenaire" suffix="%" testid="settings-partner-share" value={settings.partner_share_pct}
            onChange={(v) => setSettings({ ...settings, partner_share_pct: v })} />
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Programme matériel</p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <NumberField label="Seuil palier 1" suffix="€" testid="settings-material-t1" value={settings.material_threshold_1}
            onChange={(v) => setSettings({ ...settings, material_threshold_1: v })} />
          <NumberField label="Avantage au palier 1" suffix="€" testid="settings-material-a1" value={settings.material_amount_1}
            onChange={(v) => setSettings({ ...settings, material_amount_1: v })} />
          <NumberField label="Seuil palier 2" suffix="€" testid="settings-material-t2" value={settings.material_threshold_2}
            onChange={(v) => setSettings({ ...settings, material_threshold_2: v })} />
          <NumberField label="Avantage palier 2 (rien consommé)" suffix="€" testid="settings-material-a2-unused" value={settings.material_amount_2_if_unused}
            onChange={(v) => setSettings({ ...settings, material_amount_2_if_unused: v })} />
          <NumberField label="Complément palier 2 (déjà consommé)" suffix="€" testid="settings-material-a2-used" value={settings.material_amount_2_extra_if_used}
            onChange={(v) => setSettings({ ...settings, material_amount_2_extra_if_used: v })} />
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Voyage CozyHome</p>
        <div className="mt-3 space-y-3">
          {settings.travel_thresholds.map((tier, i) => (
            <div key={i} data-testid={`settings-travel-tier-${i}`} className="grid gap-3 rounded-xl border border-slate-100 p-3 sm:grid-cols-3">
              <NumberField label="Seuil" suffix="€" testid={`settings-travel-amount-${i}`} value={tier.amount}
                onChange={(v) => updateTravelTier(i, "amount", v)} />
              <NumberField label="Participation partenaire" suffix="%" testid={`settings-travel-pct-${i}`} value={tier.partner_pct}
                onChange={(v) => updateTravelTier(i, "partner_pct", v)} />
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Libellé</span>
                <input data-testid={`settings-travel-label-${i}`} value={tier.label} onChange={(e) => updateTravelTier(i, "label", e.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
              </label>
            </div>
          ))}
          <NumberField label="Budget voyage max / partenaire" suffix="€" testid="settings-travel-budget" value={settings.travel_budget_max_per_partner}
            onChange={(v) => setSettings({ ...settings, travel_budget_max_per_partner: v })} />
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Prime exceptionnelle</p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <NumberField label="Seuil prime" suffix="€" testid="settings-bonus-threshold" value={settings.bonus_threshold}
            onChange={(v) => setSettings({ ...settings, bonus_threshold: v })} />
          <NumberField label="Montant de la prime" suffix="€" testid="settings-bonus-amount" value={settings.bonus_amount}
            onChange={(v) => setSettings({ ...settings, bonus_amount: v })} />
        </div>
      </div>

      <button data-testid="settings-save" disabled={saving} onClick={save}
        className="mt-5 flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer les réglages
      </button>
    </div>
  );
};
